import { mergeRefs } from '@grupoboticario/flora-react-utils'
import React, { useRef, useState, useEffect } from 'react'
import { numberMask, patternMask } from './utils'

export type NumberMaskProps = {
  type?: string
  locale?: string
  style?: string
  currency?: string
  minimumIntegerDigits?: number
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  minimumSignificantDigits?: number
  maximumSignificantDigits?: number
  maximumIntegerDigits?: number
}

/**
 * @description Mask type
 * @type {string} - 'xx,xx'
 * @type {function} - (value: string) => string
 * @type {NumberMaskProps}
 */
export type MaskProps = string | ((value: string) => string) | NumberMaskProps

const getMaskedValue = (value: string, mask: MaskProps) => {
  if (typeof mask === 'string') {
    return patternMask(value, mask)
  } else if (typeof mask === 'function') {
    return mask(value)
  } else if (typeof mask === 'object') {
    return numberMask(value, mask)
  }
  return value
}

export type ReturnMaskProps = {
  ref: React.Ref<any>
  value?: string
  defaultValue?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

export type UseMaskProps = {
  ref?: any
  mask?: MaskProps
  value?: string
  defaultValue?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

export function useMask(props: UseMaskProps): ReturnMaskProps {
  const { mask, ref, value, defaultValue, onChange, onKeyDown } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const [defaultValueMasked, setDefaultValueMasked] = useState('')

  useEffect(() => {
    if (!!mask) {
      setDefaultValueMasked(getMaskedValue(value || defaultValue || '', mask))
    }
  }, [mask, value, defaultValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mask) {
      let newValue = getMaskedValue((inputRef.current as HTMLInputElement)?.value, mask) || ''
      ;(inputRef.current as HTMLInputElement).value = newValue
      e.target.value = newValue
      setDefaultValueMasked(newValue)
    }
    if (onChange) onChange(e)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isKeyDelete = e.key?.toLowerCase() === 'backspace' || e.keyCode === 8
    if (isKeyDelete && typeof mask === 'object' && mask.style === 'percent') {
      const value = (inputRef.current as HTMLInputElement)?.value
      const newValue = value.replace('%', '')
      if (inputRef.current?.selectionStart !== newValue.length) {
        // delete only if cursor is at the end
        ;(inputRef.current as HTMLInputElement).value = newValue.slice(0, newValue.length)
      }
    }
    if (onKeyDown) onKeyDown(e)
  }

  return {
    value: defaultValueMasked || value,
    defaultValue: defaultValueMasked || defaultValue,
    onChange: handleChange,
    onKeyDown: handleKeyPress,
    ref: mergeRefs(ref, inputRef),
  }
}
